import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { API_BASE } from '../config/api';
import useIsMobile from '../utils/useIsMobile';

const AdminDashboard = () => {
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  const [stats, setStats] = useState({
    products: 0, categories: 0, blogs: 0, jobs: 0, media: 0, gallery: 0
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchStats = async () => {
      try {
        const [prodRes, catRes, blogRes, jobRes, mediaRes, galRes] = await Promise.all([
          fetch(`${API_BASE}/products`),
          fetch(`${API_BASE}/categories`),
          fetch(`${API_BASE}/blogs`),
          fetch(`${API_BASE}/jobs`),
          fetch(`${API_BASE}/media`),
          fetch(`${API_BASE}/gallery`)
        ]);
        const [prods, cats, blogs, jobs, media, gallery] = await Promise.all([
          prodRes.json(), catRes.json(), blogRes.json(),
          jobRes.json(), mediaRes.json(), galRes.json()
        ]);
        setStats({
          products: Array.isArray(prods) ? prods.length : 0,
          categories: Array.isArray(cats) ? cats.length : 0,
          blogs: Array.isArray(blogs) ? blogs.length : 0,
          jobs: Array.isArray(jobs) ? jobs.length : 0,
          media: Array.isArray(media?.items) ? media.items.length : 0,
          gallery: Array.isArray(gallery?.items) ? gallery.items.length : 0,
        });
      } catch (err) {
        console.error('Error fetching dashboard stats:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchStats();
  }, []);

  const statCards = [
    { label: 'Total Products', value: stats.products, icon: 'Package', color: '#FFD700', bg: 'rgba(255,215,0,0.12)', path: '/admin/products' },
    { label: 'Categories', value: stats.categories, icon: 'FolderTree', color: '#60A5FA', bg: 'rgba(96,165,250,0.12)', path: '/admin/categories' },
    { label: 'Blog Posts', value: stats.blogs, icon: 'FileText', color: '#A78BFA', bg: 'rgba(167,139,250,0.12)', path: '/admin/blogs' },
    { label: 'Job Openings', value: stats.jobs, icon: 'Briefcase', color: '#34D399', bg: 'rgba(52,211,153,0.12)', path: '/admin/jobs' },
    { label: 'Media Files', value: stats.media, icon: 'Image', color: '#F472B6', bg: 'rgba(244,114,182,0.12)', path: '/admin/media' },
    { label: 'Gallery Images', value: stats.gallery, icon: 'Images', color: '#FB923C', bg: 'rgba(251,146,60,0.12)', path: '/admin/gallery' },
  ];

  const quickActions = [
    { title: 'Add New Product', desc: 'List a new product in your store', icon: 'PlusCircle', path: '/admin/products/new', color: '#FFD700' },
    { title: 'Upload Media', desc: 'Add images or videos to your library', icon: 'Upload', path: '/admin/media/new', color: '#60A5FA' },
    { title: 'Create Blog Post', desc: 'Write and publish a new article', icon: 'Edit', path: '/admin/blogs/new', color: '#A78BFA' },
    { title: 'Post a Job', desc: 'Open a new career opportunity', icon: 'UserPlus', path: '/admin/jobs/new', color: '#34D399' },
    { title: 'Manage Categories', desc: 'Organize your product groups', icon: 'Layers', path: '/admin/categories', color: '#F472B6' },
    { title: 'Upload Gallery', desc: 'Add images to the gallery', icon: 'Camera', path: '/admin/gallery', color: '#FB923C' },
  ];

  const getIconSvg = (name) => {
    const icons = {
      Package: <><path d="M16.5 9.4 7.55 4.24"/><path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z"/><polyline points="3.29 7 12 12 20.71 7"/><line x1="12" y1="22" x2="12" y2="12"/></>,
      FolderTree: <><path d="M20 10a1 1 0 0 0 1-1V6a1 1 0 0 0-1-1h-5.5a1 1 0 0 1-.8-.4l-1.4-1.8A1 1 0 0 0 11.5 2H5a1 1 0 0 0-1 1v14a1 1 0 0 0 1 1h6"/><path d="M2 10h4"/><path d="M2 6h4"/><path d="M2 2h4"/><path d="m21 14-4 4-2-2"/><path d="M14 14h7v5"/></>,
      FileText: <><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/><line x1="16" y1="13" x2="8" y2="13"/><line x1="16" y1="17" x2="8" y2="17"/><polyline points="10 9 9 9 8 9"/></>,
      Briefcase: <><rect x="2" y="7" width="20" height="14" rx="2" ry="2"/><path d="M16 21V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v16"/></>,
      Image: <><rect x="3" y="3" width="18" height="18" rx="2" ry="2"/><circle cx="8.5" cy="8.5" r="1.5"/><polyline points="21 15 16 10 5 21"/></>,
      Images: <><path d="M18 22H4a2 2 0 0 1-2-2V6"/><path d="m22 13-1.3-1.3a2.3 2.3 0 0 0-3.2 0L11 18"/><rect x="8" y="2" width="16" height="16" rx="2" ry="2"/><circle cx="16" cy="10" r="1.5"/><path d="M7 14v5a1 1 0 0 0 1 1h3"/></>,
    };
    const actionIcons = {
      PlusCircle: <><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="16"/><line x1="8" y1="12" x2="16" y2="12"/></>,
      Upload: <><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="17 8 12 3 7 8"/><line x1="12" y1="3" x2="12" y2="15"/></>,
      Edit: <><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/></>,
      UserPlus: <><path d="M16 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="8.5" cy="7" r="4"/><line x1="20" y1="8" x2="20" y2="14"/><line x1="23" y1="11" x2="17" y2="11"/></>,
      Layers: <><polygon points="12 2 2 7 12 12 22 7 12 2"/><polyline points="2 17 12 22 22 17"/><polyline points="2 12 12 17 22 12"/></>,
      Camera: <><path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z"/><circle cx="12" cy="13" r="4"/></>,
    };
    return icons[name] || actionIcons[name] || null;
  };

  const renderIcon = (name, size = 24) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      {getIconSvg(name)}
    </svg>
  );

  return (
    <div style={{ animation: 'fadeIn 0.3s ease-in-out' }}>
      {/* Welcome Section */}
      <div style={{
        background: 'linear-gradient(135deg, #0F1B2D 0%, #1A2744 50%, #0F1B2D 100%)',
        borderRadius: '16px',
        padding: isMobile ? '20px' : '28px 32px',
        border: '1px solid rgba(255,215,0,0.08)',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        gap: '20px',
        flexWrap: 'wrap',
        marginBottom: '24px'
      }}>
        <div>
          <h1 style={{ margin: 0, fontSize: '24px', fontWeight: 700, color: '#F9FAFB', letterSpacing: '-0.3px' }}>
            Welcome back, Admin
          </h1>
          <p style={{ margin: '6px 0 0', color: '#9CA3AF', fontSize: '15px' }}>
            Here's what's happening with your store today.
          </p>
        </div>
        <div style={{ display: 'flex', gap: '8px' }}>
          <button
            onClick={() => navigate('/')}
            style={{
              padding: '10px 20px',
              borderRadius: '10px',
              border: '1px solid rgba(255,215,0,0.25)',
              background: 'transparent',
              cursor: 'pointer',
              color: '#FFD700',
              fontWeight: 600,
              fontSize: '14px',
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              transition: 'all 0.2s'
            }}
            onMouseEnter={(e) => { e.currentTarget.style.background = 'rgba(255,215,0,0.1)'; }}
            onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
          >
            {renderIcon('Upload', 16)}
            View Site
          </button>
        </div>
      </div>

      {/* Stats Grid */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))',
        gap: '16px',
        marginBottom: '24px'
      }}>
        {statCards.map((card) => (
          <div
            key={card.label}
            onClick={() => navigate(card.path)}
            style={{
              background: '#0F1B2D',
              borderRadius: '14px',
              padding: '20px',
              border: '1px solid rgba(255,255,255,0.05)',
              cursor: 'pointer',
              transition: 'all 0.25s ease',
              position: 'relative',
              overflow: 'hidden'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.borderColor = 'rgba(255,215,0,0.3)';
              e.currentTarget.style.transform = 'translateY(-3px)';
              e.currentTarget.style.boxShadow = '0 8px 25px rgba(0,0,0,0.3)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.borderColor = 'rgba(255,255,255,0.05)';
              e.currentTarget.style.transform = 'translateY(0)';
              e.currentTarget.style.boxShadow = 'none';
            }}
          >
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'flex-start',
              marginBottom: '16px'
            }}>
              <div style={{
                width: '44px',
                height: '44px',
                borderRadius: '12px',
                backgroundColor: card.bg,
                color: card.color,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}>
                {renderIcon(card.icon, 20)}
              </div>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#6B7280" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}>
                <line x1="5" y1="12" x2="19" y2="12"/><polyline points="12 5 19 12 12 19"/>
              </svg>
            </div>
            <div>
              <p style={{ margin: 0, fontSize: '13px', color: '#9CA3AF', fontWeight: 500 }}>{card.label}</p>
              <h3 style={{ margin: '4px 0 0', fontSize: '28px', fontWeight: 700, color: '#F9FAFB', letterSpacing: '-0.5px' }}>
                {loading ? (
                  <span style={{ fontSize: '20px', color: '#6B7280' }}>...</span>
                ) : (
                  card.value
                )}
              </h3>
            </div>
          </div>
        ))}
      </div>

      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(350px, 1fr))',
        gap: '24px'
      }}>
        {/* Quick Actions */}
        <div style={{
          background: '#0F1B2D',
          borderRadius: '14px',
          border: '1px solid rgba(255,255,255,0.05)',
          overflow: 'hidden'
        }}>
          <div style={{
            padding: '20px 24px',
            borderBottom: '1px solid rgba(255,255,255,0.05)',
            display: 'flex',
            alignItems: 'center',
            gap: '10px'
          }}>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#FFD700" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2"/>
            </svg>
            <h3 style={{ margin: 0, fontSize: '16px', fontWeight: 600, color: '#F9FAFB' }}>Quick Actions</h3>
          </div>
          <div style={{ padding: '16px', display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px' }}>
            {quickActions.map((action) => (
              <button
                key={action.title}
                onClick={() => navigate(action.path)}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '12px',
                  padding: '14px',
                  borderRadius: '10px',
                  border: '1px solid rgba(255,255,255,0.05)',
                  background: 'rgba(255,255,255,0.02)',
                  cursor: 'pointer',
                  textAlign: 'left',
                  width: '100%',
                  transition: 'all 0.2s'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = 'rgba(255,255,255,0.05)';
                  e.currentTarget.style.borderColor = 'rgba(255,215,0,0.2)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = 'rgba(255,255,255,0.02)';
                  e.currentTarget.style.borderColor = 'rgba(255,255,255,0.05)';
                }}
              >
                <div style={{ color: action.color, flexShrink: 0, display: 'flex' }}>
                  {renderIcon(action.icon, 18)}
                </div>
                <div style={{ minWidth: 0 }}>
                  <div style={{ fontSize: '13px', fontWeight: 600, color: '#F3F4F6', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                    {action.title}
                  </div>
                  <div style={{ fontSize: '11px', color: '#6B7280', marginTop: '2px' }}>
                    {action.desc}
                  </div>
                </div>
              </button>
            ))}
          </div>
        </div>

        {/* System Info */}
        <div style={{
          background: '#0F1B2D',
          borderRadius: '14px',
          border: '1px solid rgba(255,255,255,0.05)',
          overflow: 'hidden'
        }}>
          <div style={{
            padding: '20px 24px',
            borderBottom: '1px solid rgba(255,255,255,0.05)',
            display: 'flex',
            alignItems: 'center',
            gap: '10px'
          }}>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#60A5FA" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="12" cy="12" r="10"/><line x1="12" y1="16" x2="12" y2="12"/><line x1="12" y1="8" x2="12.01" y2="8"/>
            </svg>
            <h3 style={{ margin: 0, fontSize: '16px', fontWeight: 600, color: '#F9FAFB' }}>System Information</h3>
          </div>
          <div style={{ padding: '20px 24px' }}>
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: '10px',
              padding: '14px 16px',
              background: 'rgba(52,211,153,0.08)',
              borderRadius: '10px',
              marginBottom: '16px'
            }}>
              <span style={{ width: '10px', height: '10px', borderRadius: '50%', background: '#34D399', flexShrink: 0 }} />
              <span style={{ fontWeight: 600, color: '#6EE7B7', fontSize: '14px' }}>All Systems Operational</span>
              <span style={{ marginLeft: 'auto', color: '#34D399', fontSize: '12px', fontWeight: 600 }}>Laravel API</span>
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '6px 0' }}>
                <span style={{ color: '#9CA3AF', fontSize: '13px' }}>Frontend</span>
                <span style={{ color: '#F3F4F6', fontWeight: 500, fontSize: '13px' }}>React 18</span>
              </div>
              <div style={{ height: '1px', background: 'rgba(255,255,255,0.05)' }} />
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '6px 0' }}>
                <span style={{ color: '#9CA3AF', fontSize: '13px' }}>Backend</span>
                <span style={{ color: '#F3F4F6', fontWeight: 500, fontSize: '13px' }}>Laravel 11</span>
              </div>
              <div style={{ height: '1px', background: 'rgba(255,255,255,0.05)' }} />
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '6px 0' }}>
                <span style={{ color: '#9CA3AF', fontSize: '13px' }}>Database</span>
                <span style={{ color: '#F3F4F6', fontWeight: 500, fontSize: '13px' }}>MySQL</span>
              </div>
              <div style={{ height: '1px', background: 'rgba(255,255,255,0.05)' }} />
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '6px 0' }}>
                <span style={{ color: '#9CA3AF', fontSize: '13px' }}>User Role</span>
                <span style={{ color: '#FFD700', fontWeight: 600, fontSize: '13px' }}>Super Admin</span>
              </div>
            </div>

            <div style={{
              marginTop: '20px',
              paddingTop: '16px',
              borderTop: '1px solid rgba(255,255,255,0.05)',
              fontSize: '12px',
              color: '#6B7280',
              fontStyle: 'italic'
            }}>
              Last sync: Just now
            </div>
          </div>
        </div>
      </div>

      <style>{`
        @keyframes fadeIn {
          from { opacity: 0; transform: translateY(10px); }
          to { opacity: 1; transform: translateY(0); }
        }
      `}</style>
    </div>
  );
};

export default AdminDashboard;
